<script setup>
import { ref, computed, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import VueHcaptcha from '@hcaptcha/vue3-hcaptcha';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { resendConfirmation } from '../../../api/auth';

const props = defineProps({
  email: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();
const router = useRouter();
const store = useStore();

const userEmail = computed(() => props.email || 'user@example.com');

const globalConfig = computed(() => store.getters['globalConfig/get']);
const isResendingEmail = ref(false);
const hCaptcha = ref(null);
let captchaToken = '';

const cooldownSeconds = ref(0);
let timer = null;

const startCooldown = (seconds = 60) => {
  cooldownSeconds.value = seconds;
  if (timer) clearInterval(timer);
  timer = setInterval(() => {
    if (cooldownSeconds.value > 0) {
      cooldownSeconds.value -= 1;
    } else {
      clearInterval(timer);
    }
  }, 1000);
};

onUnmounted(() => {
  if (timer) clearInterval(timer);
});

const performResend = async () => {
  isResendingEmail.value = true;
  try {
    await resendConfirmation({
      email: userEmail.value,
      hCaptchaClientResponse: captchaToken,
    });
    useAlert(t('REGISTER.VERIFY_EMAIL.RESEND_SUCCESS'));
    startCooldown(60);
  } catch {
    useAlert(t('REGISTER.VERIFY_EMAIL.RESEND_ERROR'));
  } finally {
    isResendingEmail.value = false;
    captchaToken = '';
    if (globalConfig.value.hCaptchaSiteKey && hCaptcha.value) {
      hCaptcha.value.reset();
    }
  }
};

const handleResendEmail = () => {
  if (isResendingEmail.value || cooldownSeconds.value > 0) return;
  if (globalConfig.value.hCaptchaSiteKey) {
    hCaptcha.value.execute();
  } else {
    performResend();
  }
};

const onCaptchaVerified = token => {
  captchaToken = token;
  performResend();
};

const onCaptchaError = () => {
  isResendingEmail.value = false;
  captchaToken = '';
  if (hCaptcha.value) hCaptcha.value.reset();
};
</script>

<template>
  <div
    class="relative w-full min-h-screen flex flex-col justify-between bg-gradient-to-br from-slate-50 via-blue-50/50 to-indigo-50/70 text-slate-800 p-4 md:p-8 select-none overflow-hidden"
  >
    <!-- Vibrant Light Mesh Orbs (Zero Image Dependency) -->
    <div
      class="absolute -top-32 -left-32 w-96 h-96 bg-blue-400/25 rounded-full blur-[110px] pointer-events-none animate-pulse"
    />
    <div
      class="absolute -bottom-32 -right-32 w-96 h-96 bg-indigo-400/30 rounded-full blur-[130px] pointer-events-none animate-pulse"
    />
    <div
      class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-sky-300/20 rounded-full blur-[150px] pointer-events-none"
    />

    <!-- Top Header -->
    <header
      class="relative z-10 w-full max-w-6xl mx-auto flex items-center justify-between py-4"
    >
      <div class="flex items-center gap-3">
        <img
          v-if="globalConfig.logo"
          :src="globalConfig.logo"
          :alt="globalConfig.installationName"
          class="h-8 w-auto block dark:hidden"
        />
        <img
          v-if="globalConfig.logoDark"
          :src="globalConfig.logoDark"
          :alt="globalConfig.installationName"
          class="h-8 w-auto hidden dark:block"
        />
        <span
          v-if="!globalConfig.logo && !globalConfig.logoDark"
          class="text-xl font-extrabold text-slate-900 tracking-tight"
        >
          {{ globalConfig.installationName || 'OS Desk' }}
        </span>
      </div>

      <router-link
        to="/app/login"
        class="text-xs font-bold text-slate-700 hover:text-slate-900 transition-colors bg-white/80 hover:bg-white backdrop-blur-md px-4 py-2 rounded-full border border-slate-200/80 shadow-sm"
      >
        Sign In &rarr;
      </router-link>
    </header>

    <!-- Main Card Section -->
    <main class="relative z-10 w-full max-w-lg mx-auto my-auto py-8">
      <div
        class="bg-white/95 backdrop-blur-2xl border border-slate-200/90 rounded-3xl shadow-2xl shadow-blue-900/5 p-8 md:p-10 space-y-8 text-center"
      >
        <!-- Hero Mail Icon Badge -->
        <div class="relative inline-flex items-center justify-center">
          <div
            class="absolute inset-0 bg-blue-500/20 rounded-full blur-xl animate-pulse"
          />
          <div
            class="relative w-20 h-20 bg-gradient-to-tr from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center shadow-xl shadow-blue-500/25 transform -rotate-3 hover:rotate-0 transition-all duration-300"
          >
            <Icon icon="i-lucide-mail-check" class="size-10 text-white" />
          </div>
        </div>

        <!-- Title & Subtitle -->
        <div class="space-y-3">
          <h1
            class="text-2xl md:text-3xl font-black text-slate-900 tracking-tight"
          >
            {{ $t('REGISTER.VERIFY_EMAIL.TITLE') }}
          </h1>
          <p class="text-sm text-slate-600 leading-relaxed max-w-sm mx-auto">
            {{ $t('REGISTER.VERIFY_EMAIL.DESCRIPTION', { email: userEmail }) }}
          </p>
        </div>

        <!-- Email Details Pill -->
        <div
          class="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 flex items-center justify-between gap-3 text-left"
        >
          <div class="flex items-center gap-3.5 overflow-hidden">
            <div
              class="w-10 h-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0 font-medium"
            >
              <Icon icon="i-lucide-mail" class="size-5" />
            </div>
            <div class="truncate">
              <div class="text-xs text-slate-500 font-medium">Sent to</div>
              <div
                class="text-sm font-bold text-slate-900 truncate"
                :title="userEmail"
              >
                {{ userEmail }}
              </div>
            </div>
          </div>
          <router-link
            to="/app/auth/signup"
            class="text-xs font-bold text-blue-600 hover:text-blue-700 hover:underline flex-shrink-0 px-2.5 py-1 rounded-md bg-blue-50"
          >
            Change
          </router-link>
        </div>

        <!-- Actions & Captcha -->
        <div class="space-y-4">
          <VueHcaptcha
            v-if="globalConfig.hCaptchaSiteKey"
            ref="hCaptcha"
            size="invisible"
            :sitekey="globalConfig.hCaptchaSiteKey"
            @verify="onCaptchaVerified"
            @error="onCaptchaError"
            @expired="onCaptchaError"
            @challenge-expired="onCaptchaError"
            @closed="onCaptchaError"
          />

          <NextButton
            lg
            type="button"
            data-testid="resend_email_button"
            class="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-3.5 px-4 rounded-xl shadow-lg shadow-blue-500/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            :label="
              cooldownSeconds > 0
                ? `Resend email in ${cooldownSeconds}s`
                : $t('REGISTER.VERIFY_EMAIL.RESEND')
            "
            :is-loading="isResendingEmail"
            :disabled="isResendingEmail || cooldownSeconds > 0"
            @click="handleResendEmail"
          />
        </div>

        <!-- Help Info Box -->
        <div
          class="pt-4 border-t border-slate-100 text-xs text-slate-500 space-y-2"
        >
          <p class="flex items-center justify-center gap-1.5">
            <Icon icon="i-lucide-info" class="size-4 text-blue-600" />
            Can't find the email? Check your spam or junk folder.
          </p>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer
      class="relative z-10 w-full max-w-6xl mx-auto text-center py-4 text-xs text-slate-500"
    >
      &copy; {{ new Date().getFullYear() }}
      {{ globalConfig.installationName || 'OS Desk' }}. All rights reserved.
    </footer>
  </div>
</template>
